import {
  FullShopArticle,
  getSellingStatus,
  SellingStatus,
  useDeleteArticle,
  useGetArticles,
} from "@/api/article";
import { localPrice, PagingParameters } from "@/api/dtos";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import { DatatablePagination } from "@/components/ui/datatable-pagination";
import { generateSortableHeader } from "@/components/ui/shared";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import i18n from "@/i18n";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArticleEditDrawer } from "./ArticleEditDrawer";
import { SellingStatusBadge } from "./SellingStatusBadge";

const ArticleDescription = ({
  sellingStatus,
  count = 2,
}: {
  sellingStatus: SellingStatus | "all";
  count?: number;
}) => {
  const { t } = useTranslation();

  switch (sellingStatus) {
    case "SOLD":
      return <>{t("eshopDashboardHeaderArticleVendu", { count })}</>;
    case "UNSOLD":
      return <>{t("eshopDashboardHeaderArticleEnVente", { count })}</>;
    case "all":
      return <>{t("eshopDashboardHeaderArticleTous", { count })}</>;
  }
};

const ArticleList = () => {
  const deleteArticle = useDeleteArticle();

  const onDeleteArticle = (articleId: string) => {
    deleteArticle.mutate(articleId, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "deleteArticleSucessMessage",
          duration: 1000,
          variant: "success",
        });
      },
      onError: () =>
        toast({
          title: "Error",
          description: "deleteArticleErrorMessage",
          duration: 1000,
          variant: "error",
        }),
    });
  };

  const navigate = useNavigate();

  const { t } = useTranslation();

  const columns: ColumnDef<FullShopArticle>[] = [
    {
      id: "recordingNumber",
      size: 1,
      header: generateSortableHeader(t("eshop.items.table.recordingNumber")),
      cell: ({ row }) => (
        <>
          <span className="font-medium inline-block w-fit text-nowrap">
            {row.original.sellableContent.recordingNumber?.toString()}
          </span>
        </>
      ),
    },
    {
      id: "title",
      header: generateSortableHeader(t("eshop.items.table.title")),
      enableResizing: true,
      size: 10000,
      cell: ({ row }) => (
        <div className="px-3 flex flex-row gap-3 items-center text-ellipsis">
          {row.original.sellableContent.previews &&
          row.original.sellableContent.previews.length > 0 ? (
            <img
              alt={`Thumbnail of ${row.original.sellableContent.title}`}
              className="size-10 aspect-square object-contain"
              src={row.original.sellableContent.previews[0]?.small ?? ""}
            ></img>
          ) : (
            <div className="size-10 bg-slate-500"></div>
          )}
          <span className="font-medium overflow-hidden text-ellipsis">
            {row.original.sellableContent.title}
          </span>
        </div>
      ),
    },
    {
      size: 1,
      id: "priceAmount",
      header: generateSortableHeader(t("eshop.items.table.priceAmount")),
      cell: ({ row }) => (
        <span>{localPrice(i18n.language, row.original.priceAmount)} €</span>
      ),
    },
    {
      size: 1,
      id: "currentQuantity",
      header: generateSortableHeader(t("eshop.items.table.status")),
      cell: ({ row }) => (
        <SellingStatusBadge status={getSellingStatus(row.original)} />
      ),
    },
    {
      header: "",
      id: "edit",
      size: 1,
      cell: ({ row }) => (
        <div className="flex flex-row">
          <Button
            variant={"link"}
            onClick={() => onDeleteArticle(row.original.id)}
          >
            <TrashIcon className="text-gray-500" />
          </Button>
          <ArticleEditDrawer variant="edit" article={row.original} />
        </div>
      ),
    },
  ];

  const [tabValue, setTabValue] = useState<SellingStatus | "all">("all");

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 20,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const { data } = useGetArticles(
    getPagination,
    sorting,
    tabValue != "all" ? tabValue : undefined,
  );

  return (
    <Tabs
      defaultValue={"all"}
      onValueChange={(v) => setTabValue(v as SellingStatus | "all")}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <div>
            <ArticleDescription
              sellingStatus={tabValue}
              count={data?.totalSize ?? 0}
            />
          </div>
          <div className="flex flex-row items-center gap-4 mb-0">
            <TabsList className="flex flex-row items-center">
              <TabsTrigger value="all">
                {t("eshopDashboardTabTous")}
              </TabsTrigger>
              <TabsTrigger value="SOLD">
                {t("eshopDashboardTabVendu")}
              </TabsTrigger>
              <TabsTrigger value="UNSOLD">
                {t("eshopDashboardTabEnVente")}
              </TabsTrigger>
            </TabsList>
            <Button
              variant={"outline"}
              className="font-semibold text-sm flex flex-row justify-between gap-1 px-3 py-2"
              onClick={() => navigate("/cms/e-shop/ajout")}
            >
              <PlusIcon className="text-slate-500 size-5" />
              {t("eshopDashboardAjouterArticleBouton")}
            </Button>
          </div>
        </div>
        <div>
          <DataTable
            columns={columns}
            data={data ? data.data : []}
            options={{
              state: {
                sorting: sorting,
              },
              onSortingChange: setSorting,
              manualSorting: true,
            }}
          ></DataTable>
        </div>
        <div>
          <DatatablePagination
            pagination={getPagination}
            setPage={setPagination}
            totalSize={data?.totalSize ?? 0}
          />
        </div>
      </div>
    </Tabs>
  );
};

export default ArticleList;
